import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import { endSession } from '../../app/slices/sessionSlice';
import { addToast } from '../../app/slices/uiSlice';
import LoadingSpinner from '../../components/LoadingSpinner';

interface EndSessionFormProps {
  onCancel: () => void;
  onSaved: (saved: any) => void;
}

export default function EndSessionForm({ onCancel, onSaved }: EndSessionFormProps) {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentSession, isLoading } = useSelector((state: RootState) => state.sessions);

  const [endOdometer, setEndOdometer] = useState('');
  const [totalKmWithPassenger, setKmWithPassenger] = useState('');
  const [grossEarnings, setGrossEarnings] = useState('');
  const [drivingDurationHours, setDrivingDurationHours] = useState('');
  const [drivingDurationMinutes, setDrivingDurationMinutes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uid || !currentSession?.startOdometer) {
      dispatch(addToast({ message: 'No active session found', type: 'error' }));
      return;
    }
    const endOdo = parseFloat(endOdometer);
    const earnings = parseFloat(grossEarnings);
    const riderKm = parseFloat(totalKmWithPassenger || '0');
    const hours = parseInt(drivingDurationHours || '0');
    const minutes = parseInt(drivingDurationMinutes || '0');

    if (isNaN(endOdo) || endOdo <= currentSession.startOdometer) {
      dispatch(
        addToast({ message: 'End odometer must be greater than start odometer', type: 'error' })
      );
      return;
    }
    if (isNaN(earnings) || earnings < 0) {
      dispatch(addToast({ message: 'Enter valid gross earnings', type: 'error' }));
      return;
    }
    const totalKm = endOdo - currentSession.startOdometer;
    if (riderKm > totalKm) {
      dispatch(addToast({ message: 'Rider km cannot exceed total km driven', type: 'error' }));
      return;
    }

    // Calculate total driving duration in minutes
    const totalDrivingMinutes = hours * 60 + minutes;
    if (totalDrivingMinutes <= 0) {
      dispatch(addToast({ message: 'Enter valid driving duration', type: 'error' }));
      return;
    }

    setIsSubmitting(true);
    try {
      const action: any = await dispatch(
        endSession({
          userId: user.uid,
          endOdometer: endOdo,
          totalKmWithPassenger: riderKm,
          grossEarnings: earnings,
          provinceCode: user.province,
          drivingDurationMinutes: totalDrivingMinutes,
        })
      );
      const saved = action.payload;
      if (saved) onSaved(saved);
    } catch {
      /* handled by slice */
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">End Odometer (km)</label>
        <input
          type="number"
          placeholder="e.g., 125150"
          value={endOdometer}
          onChange={e => setEndOdometer(e.target.value)}
          className="input"
        />
      </div>
      <div>
        <label className="label">KM with Passenger</label>
        <input
          type="number"
          placeholder="e.g., 120"
          value={totalKmWithPassenger}
          onChange={e => setKmWithPassenger(e.target.value)}
          className="input"
        />
        <p className="text-xs text-gray-500 mt-1">
          At the end of a driving session, Uber shows total kilometres with a passenger. Put that
          number here.
        </p>
      </div>
      <div>
        <label className="label">Gross Earnings (CAD)</label>
        <input
          type="text"
          inputMode="decimal"
          placeholder="e.g., 85.50"
          value={grossEarnings}
          onChange={e => setGrossEarnings(e.target.value.replace(',', '.'))}
          className="input"
        />
      </div>
      <div>
        <label className="label">Driving Duration</label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="number"
              placeholder="Hours"
              min="0"
              max="24"
              value={drivingDurationHours}
              onChange={e => setDrivingDurationHours(e.target.value)}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Hours</p>
          </div>
          <div className="flex-1">
            <input
              type="number"
              placeholder="Minutes"
              min="0"
              max="59"
              value={drivingDurationMinutes}
              onChange={e => setDrivingDurationMinutes(e.target.value)}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Minutes</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Uber will show you the total time you drove during the session. Enter this duration here.
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting || isLoading}
        >
          Cancel
        </button>
        <button type="submit" className="btn-warning" disabled={isSubmitting || isLoading}>
          {isSubmitting || isLoading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" /> Ending...
            </>
          ) : (
            'End Session'
          )}
        </button>
      </div>
    </form>
  );
}
